"use client";

import * as React from "react";
import Link from "next/link";
import { motion } from "motion/react";
import {
  Lock,
  Sparkles,
  Shield,
  ShieldCheck,
  Target,
  Layers,
  ArrowRight,
  Crown,
  Mic,
  Activity,
  CheckCircle2,
  BookOpen,
  KeyRound,
  Eye,
  Cpu,
} from "lucide-react";
import { PageHero } from "@/components/ui/page-hero";
import { Section } from "@/components/layout/section";
import { RelatedPages } from "@/components/ui/related-pages";
import { AccordionBlock } from "@/lib/blocks/accordion-block";
import { CounterBlock } from "@/lib/blocks/counter-block";
import { PillarGridBlock } from "@/lib/blocks/pillar-grid-block";

const ACCENT: Record<string, { text: string; bg: string; border: string; ring: string; pill: string }> = {
  red: { text: "text-red", bg: "from-red/10", border: "border-red/30", ring: "ring-red/20", pill: "glass-tinted-red border-red/20" },
  purple: { text: "text-purple", bg: "from-purple/10", border: "border-purple/30", ring: "ring-purple/20", pill: "glass-tinted-purple border-purple/20" },
  cyan: { text: "text-cyan", bg: "from-cyan/10", border: "border-cyan/30", ring: "ring-cyan/20", pill: "glass-tinted-cyan border-cyan/20" },
  emerald: { text: "text-emerald", bg: "from-emerald/10", border: "border-emerald/30", ring: "ring-emerald/20", pill: "glass-tinted-emerald border-emerald/20" },
  "amber-gold": { text: "text-amber-gold", bg: "from-amber-gold/10", border: "border-amber-gold/30", ring: "ring-amber-gold/20", pill: "glass-tinted-amber-gold border-amber-gold/20" },
  denim: { text: "text-denim", bg: "from-denim/10", border: "border-denim/30", ring: "ring-denim/20", pill: "glass-tinted-denim border-denim/20" },
  lime: { text: "text-lime", bg: "from-lime/10", border: "border-lime/30", ring: "ring-lime/20", pill: "glass-tinted-lime border-lime/20" },
  coral: { text: "text-coral", bg: "from-coral/10", border: "border-coral/30", ring: "ring-coral/20", pill: "glass-tinted-coral border-coral/20" },
};

const EVIDENCE = [
  { metric: "0", suffix: "", label: "Security Breaches", sub: "Across 15+ years", accent: "emerald" },
  { metric: "99.9999", suffix: "%", label: "Uptime", sub: "31.5 seconds max/year", accent: "denim" },
  { metric: "7", suffix: "", label: "Security Layers", sub: "Zero-trust defense-in-depth", accent: "purple" },
  { metric: "5", suffix: "", label: "CryptoSuite Products", body: "End-to-end encryption", accent: "amber-gold" },
];

const SOVEREIGNTY_LAYERS = [
  { title: "Communication Sovereignty", body: "End-to-end encryption for all communications with complete metadata elimination. No sender, recipient, timestamp, or network information survives transmission." },
  { title: "Infrastructure Sovereignty", body: "Zero-trust architecture with seven independent security layers. Micro-segmentation. Software-defined perimeters. Hardware-accelerated encryption at 100 Gbps." },
  { title: "Data Sovereignty", body: "Always-encrypted storage with client-side encryption. Customer-controlled keys in Hardware Security Modules. Cryptographic data sharding through secret sharing." },
  { title: "Counter-Surveillance Sovereignty", body: "Active surveillance detection and countermeasure systems. Network traffic analysis. Signal detection. Behavioral anomaly recognition. Footprint minimization." },
  { title: "Crisis Response Sovereignty", body: "Breach containment, forensic investigation, system restoration, and post-incident hardening activated within seconds of threat detection." },
];

const PRINCIPLES = [
  { title: "Encryption by Default", body: "Every communication, every file, every transmission encrypted before it leaves the device. No unencrypted data path exists at any layer." },
  { title: "Zero-Knowledge Architecture", body: "Data encrypted on the client device before reaching any server. Even the platform operator cannot access client data. No backdoors. No escrow keys." },
  { title: "Metadata Elimination", body: "All metadata stripped at the protocol level. No record of who communicated, when, for how long, from which device, or through which network." },
  { title: "Customer-Controlled Keys", body: "Encryption keys remain in customer-controlled Hardware Security Modules. Bring Your Own Key and Hold Your Own Key architectures fully supported." },
  { title: "Air-Gap Capability", body: "Complete functionality without internet connectivity. Local threat detection and signature updates from air-gapped update stations." },
  { title: "Post-Quantum Readiness", body: "NIST-standardized post-quantum algorithms deployed today. Hybrid encryption combining classical and post-quantum key exchange." },
  { title: "Defense in Depth", body: "Seven independent security layers. Compromise at any layer contained and neutralized before propagation." },
  { title: "Continuous Verification", body: "No device, user, or connection trusted by default. Every access request verified. Every session monitored. Every anomaly triggers response." },
];

const COMPLIANCE = [
  { standard: "GDPR", scope: "EU data protection, breach notification", status: "Full compliance" },
  { standard: "ISO 27001", scope: "International information security", status: "Certified" },
  { standard: "SOC 2 Type II", scope: "Service organization controls", status: "Certified" },
  { standard: "FIPS 140-3 Level 3", scope: "Federal cryptographic modules", status: "Certified" },
  { standard: "Common Criteria EAL5+", scope: "International IT security evaluation", status: "Evaluated" },
  { standard: "HIPAA", scope: "US healthcare data privacy", status: "Full compliance" },
];

const APPLICATIONS = [
  { title: "Sovereign Application", body: "Governments, defense agencies, and sovereign institutions. End-to-end encrypted communications, classified data protection, multi-agency coordination, threat intelligence, crisis response.", accent: "red" },
  { title: "Enterprise Application", body: "Fortune-scale corporations. Executive communications, intellectual property protection, regulatory compliance, crisis response. Three hundred forty-seven percent average ROI over three years.", accent: "purple" },
  { title: "Air-Gap Deployment", body: "Classified environments and sensitive compartmented information facilities. Complete offline functionality for environments where internet connectivity is neither available nor permitted.", accent: "cyan" },
];

export default function PrivacyInformationSecurityPage() {
  return (
    <>
      <PageHero
        icon={Lock}
        overline="Privacy & Information Security"
        overlineClassName="text-denim"
        title={
          <>
            Sovereign-Grade Protection for the <span className="text-gradient-premium">World&apos;s Most Sensitive Operations.</span>
          </>
        }
        subtitle="Privacy is not a feature. It is the difference between survival and catastrophe. The security architecture Dr. Jyoti Kush oversees has maintained zero security incidents across all engagements, across eighteen countries, across every global disruption. The uptime: ninety-nine point nine nine nine nine percent. The maximum permitted downtime per year: thirty-one point five seconds."
        breadcrumbs={[{ label: "Home", href: "/" }, { label: "Expertise", href: "/expertise/" }, { label: "Privacy & Information Security" }]}
        badge="Zero compromise. Zero breach. Zero trace."
      />

      <Section background="default" className="py-16 lg:py-20">
        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-10 items-start">
          <div className="lg:sticky lg:top-24">
            <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.denim.pill} mb-5`}>
              <Sparkles className="size-3.5 text-denim" />
              <span className="text-overline text-denim">The Security Imperative</span>
            </div>
            <h2 className="text-display-md mb-5">
              Privacy is the substrate upon which all other capabilities depend.
            </h2>
            <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
              At sovereign scale, privacy is not a compliance requirement. It is an existential necessity. The reputation management campaign is meaningless if the strategy sessions are intercepted. The political campaign is compromised before it begins if the war room communications are monitored. The intelligence operation is inert if its communications are transparent.
            </p>
            <p className="text-body-md text-muted-foreground leading-relaxed">
              The standard: zero compromise, zero breach, zero trace. Every communication must be encrypted before it leaves the device. Every byte must be unreadable to any entity except the intended recipient. Every record of communication must be destroyed at the protocol level. This standard is not aspirational. It is operational — enforced across fifteen-plus years of sovereign-scale engagements.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            {EVIDENCE.map((e, i) => {
              const a = ACCENT[e.accent];
              return (
                <motion.div
                  key={e.label}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-30px" }}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className={`glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all`}
                >
                  <div className={`text-display-sm font-bold ${a.text} leading-none mb-2`}>{e.metric}{e.suffix}</div>
                  <div className="text-caption font-bold uppercase tracking-wider mb-1">{e.label}</div>
                  <div className="text-caption text-muted-foreground">{e.sub || e.body}</div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.purple.pill} mb-4`}>
            <Layers className="size-3.5 text-purple" />
            <span className="text-overline text-purple">The Five-Layer Sovereignty Architecture</span>
          </div>
          <h2 className="text-display-md mb-4">The complete framework for digital sovereignty.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Privacy is not a single product. It is a five-layer architecture where each layer addresses a distinct dimension of digital sovereignty.
          </p>
        </div>
        <div className="space-y-3">
          {SOVEREIGNTY_LAYERS.map((l, i) => {
            const a = ACCENT[["red", "purple", "cyan", "emerald", "amber-gold"][i]];
            return (
              <motion.div
                key={l.title}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`group glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.005] transition-all`}
              >
                <div className="grid lg:grid-cols-[auto_1fr] gap-5 items-center">
                  <div className={`size-14 rounded-xl bg-gradient-sovereign flex items-center justify-center shrink-0 shadow-lg`}>
                    <span className="text-body-md font-bold text-white">L{`0${i + 1}`}</span>
                  </div>
                  <div>
                    <h3 className={`text-heading-md mb-1 ${a.text}`}>{l.title}</h3>
                    <p className="text-caption text-muted-foreground leading-relaxed">{l.body}</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.cyan.pill} mb-4`}>
            <KeyRound className="size-3.5 text-cyan" />
            <span className="text-overline text-cyan">The Eight Privacy Enforcement Principles</span>
          </div>
          <h2 className="text-display-md mb-4">Non-negotiable architectural commitments.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Eight principles form the non-negotiable foundation of every information security engagement. Architectural commitments, not guidelines.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {PRINCIPLES.map((p, i) => {
            const accents = ["red", "purple", "cyan", "emerald", "amber-gold", "denim", "lime", "coral"];
            const a = ACCENT[accents[i]];
            return (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.04 }}
                className={`group relative glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all`}
              >
                <div className={`text-caption font-bold ${a.text} opacity-50 mb-2`}>P{String(i + 1).padStart(2, "0")}</div>
                <h3 className={`text-heading-md mb-2 ${a.text}`}>{p.title}</h3>
                <p className="text-caption text-muted-foreground leading-relaxed">{p.body}</p>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT["amber-gold"].pill} mb-4`}>
            <ShieldCheck className="size-3.5 text-amber-gold" />
            <span className="text-overline text-amber-gold">Compliance and Certifications</span>
          </div>
          <h2 className="text-display-md mb-4">Validated to the most demanding international standards.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Certified standards, independently audited, continuously maintained. The compliance posture covers the full spectrum of global privacy and security regulations.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
          {COMPLIANCE.map((c, i) => {
            const accents = ["red", "purple", "cyan", "emerald", "amber-gold", "denim"];
            const a = ACCENT[accents[i]];
            return (
              <motion.div
                key={c.standard}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className={`glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border}`}
              >
                <div className="flex items-start justify-between gap-3 mb-2">
                  <h3 className={`text-heading-md ${a.text}`}>{c.standard}</h3>
                  <CheckCircle2 className={`size-4 ${a.text} shrink-0`} />
                </div>
                <p className="text-caption text-muted-foreground mb-2">{c.scope}</p>
                <p className={`text-caption font-semibold ${a.text}`}>{c.status}</p>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.emerald.pill} mb-4`}>
            <Target className="size-3.5 text-emerald" />
            <span className="text-overline text-emerald">Three Application Domains</span>
          </div>
          <h2 className="text-display-md mb-4">Sovereign-grade protection across every altitude.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The same architecture that protects sovereign governments applies to the corporate domain — with adaptations calibrated to the specific challenges of each engagement.
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          {APPLICATIONS.map((a, i) => {
            const ac = ACCENT[a.accent];
            return (
              <motion.div
                key={a.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className={`glass-card rounded-2xl p-6 bg-gradient-to-br ${ac.bg} to-transparent border ${ac.border}`}
              >
                <h3 className={`text-heading-md mb-3 ${ac.text}`}>{a.title}</h3>
                <p className="text-body-sm text-muted-foreground leading-relaxed">{a.body}</p>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20" id="security-accordion" ariaLabel="Security Layers Accordion">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.denim.pill} mb-4`}>
            <Shield className="size-3.5 text-denim" />
            <span className="text-overline text-denim">The Security Layers Accordion</span>
          </div>
          <h2 className="text-display-md mb-4">Ten layers. Search the security architecture.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The signature compilation. Each layer addresses a distinct attack surface. The layers are not redundant — they are complementary. Together they produce the zero-breach record.
          </p>
        </div>
        <AccordionBlock
          searchable
          items={[
            { q: "Layer 01: Perimeter Defense", a: "Zero-trust network access, software-defined perimeters, hardware-accelerated encryption at one hundred gigabits per second. Every connection request verified before resources are exposed. The traditional firewall is insufficient. Perimeter defense operates at the protocol level.", category: "Infrastructure" },
            { q: "Layer 02: Identity and Access", a: "Multi-factor authentication, biometric verification, hardware security module key custody. Zero standing privilege — every access request is authorized in real time, with full audit trail. Customer-controlled keys. Bring Your Own Key and Hold Your Own Key architectures fully supported.", category: "Identity" },
            { q: "Layer 03: Cryptographic Foundation", a: "Five integrated encryption products across the CryptoSuite. NIST-standardized post-quantum algorithms deployed today. Hybrid encryption combining classical and post-quantum key exchange. Every byte encrypted before leaving the device — no unencrypted data path exists at any layer.", category: "Cryptography" },
            { q: "Layer 04: Data Protection", a: "Always-encrypted storage with client-side encryption. Customer-controlled keys in Hardware Security Modules. Cryptographic data sharding through secret sharing — no single party can reconstruct the data alone. Zero-knowledge architecture: even the platform operator cannot access client data.", category: "Data" },
            { q: "Layer 05: Communications Security", a: "End-to-end encryption for all communications with complete metadata elimination. No sender, recipient, timestamp, or network information survives transmission. Zero-trace communications. No phone records, no email trails, no metadata persistence.", category: "Communications" },
            { q: "Layer 06: Network Defense", a: "Micro-segmentation across the entire infrastructure. East-west traffic inspection. Anomaly detection at packet level. Active surveillance detection and countermeasure systems. Network traffic analysis, signal detection, behavioral anomaly recognition, footprint minimization.", category: "Network" },
            { q: "Layer 07: Application Security", a: "Secure software development lifecycle. Static and dynamic application security testing. Runtime application self-protection. Code review with cryptographic verification. Continuous integration security gates. Every line of code is auditable.", category: "Application" },
            { q: "Layer 08: Endpoint Protection", a: "Endpoint detection and response on every device. Behavioral analysis at the kernel level. Hardware-rooted trust attestation. Self-healing capabilities for compromised devices. Air-gap capability for classified environments where internet connectivity is neither available nor permitted.", category: "Endpoint" },
            { q: "Layer 09: Counter-Surveillance", a: "Active surveillance detection and countermeasure systems. Network traffic analysis, signal detection, behavioral anomaly recognition, footprint minimization. Counter-intelligence capabilities for environments where the adversary is actively attempting to infiltrate.", category: "Surveillance" },
            { q: "Layer 10: Crisis Response", a: "Breach containment, forensic investigation, system restoration, and post-incident hardening activated within seconds of threat detection. The crisis response layer is not theoretical — it is the operational discipline that has produced zero security incidents across fifteen-plus years of sovereign-scale operations.", category: "Response" },
          ]}
        />
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.purple.pill} mb-4`}>
            <Cpu className="size-3.5 text-purple" />
            <span className="text-overline text-purple">The CryptoSuite — Five Encryption Products</span>
          </div>
          <h2 className="text-display-md mb-4">Five products. One cryptographic fabric.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The CryptoSuite is the integrated encryption layer that powers sovereign-grade operations. Five products, each addressing a distinct operational domain, each validated against the most demanding international standards.
          </p>
        </div>
        <PillarGridBlock
          columns={3}
          variant="card"
          pillars={[
            { title: "Communications Encryption", body: "End-to-end encryption for voice, video, and messaging. Metadata elimination at the protocol level. Zero-trace communications across all channels.", icon: <ShieldCheck className="size-5" />, accent: "red" },
            { title: "Storage Encryption", body: "Always-encrypted data at rest with client-side encryption. Customer-controlled keys. Cryptographic data sharding through secret sharing.", icon: <Lock className="size-5" />, accent: "purple" },
            { title: "Network Encryption", body: "Hardware-accelerated encryption at one hundred gigabits per second. Zero-trust network access. Software-defined perimeters.", icon: <Layers className="size-5" />, accent: "cyan" },
            { title: "Key Management", body: "Hardware Security Module key custody. Bring Your Own Key and Hold Your Own Key architectures. Cryptographic key rotation and recovery.", icon: <KeyRound className="size-5" />, accent: "emerald" },
            { title: "Post-Quantum Algorithms", body: "NIST-standardized post-quantum cryptography. Hybrid encryption combining classical and quantum-resistant key exchange. Future-proof security posture.", icon: <Cpu className="size-5" />, accent: "amber-gold" },
            { title: "Air-Gap Functionality", body: "Complete offline capability for classified environments. Local threat detection and signature updates from air-gapped update stations.", icon: <Eye className="size-5" />, accent: "denim" },
          ]}
        />
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.cyan.pill} mb-4`}>
            <Activity className="size-3.5 text-cyan" />
            <span className="text-overline text-cyan">Verified Security Posture</span>
          </div>
          <h2 className="text-display-md mb-4">The record that defines sovereign-grade.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Every metric traces to operational data from sovereign-scale deployments. The numbers represent verified outcomes — not projections, not aspirations, not theoretical benchmarks.
          </p>
        </div>
        <CounterBlock
          columns={4}
          stats={[
            { value: 0, label: "Security Breaches", description: "Across all deployments, across fifteen-plus years", accent: "emerald" },
            { value: 99.9999, suffix: "%", label: "Uptime", description: "Maximum thirty-one point five seconds of downtime per year", accent: "denim" },
            { value: 7, label: "Security Layers", description: "Zero-trust defense-in-depth architecture", accent: "purple" },
            { value: 5, label: "CryptoSuite Products", description: "End-to-end cryptographic coverage", accent: "amber-gold" },
            { value: 100, suffix: " Gbps", label: "Encryption Throughput", description: "Hardware-accelerated performance", accent: "red" },
            { value: 347, suffix: "%", label: "Three-Year ROI", description: "Average return on security investment", accent: "cyan" },
          ]}
        />
      </Section>

      <Section background="default" className="py-20 lg:py-28">
        <div className="relative">
          <div aria-hidden className="absolute -top-20 -left-20 w-96 h-96 rounded-full bg-denim/15 blur-3xl pointer-events-none" />
          <div aria-hidden className="absolute -bottom-20 -right-20 w-96 h-96 rounded-full bg-emerald/15 blur-3xl pointer-events-none" />
          <div className="relative glass-strong rounded-3xl p-8 md:p-12 lg:p-16 gradient-border text-center">
            <Sparkles className="size-10 text-denim mx-auto mb-4" />
            <h2 className="text-display-md mb-3">Engage sovereign-grade security.</h2>
            <p className="text-body-lg text-muted-foreground max-w-2xl mx-auto mb-8">
              For clients whose exposure is not an option. National security level confidentiality from first contact. Every engagement begins with security architecture review.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                href="/contact/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md bg-gradient-sovereign text-white font-semibold shadow-lg hover:scale-105 transition-transform"
              >
                <Mic className="size-4" />
                Begin an Engagement
                <ArrowRight className="size-4" />
              </Link>
              <Link
                href="/expertise/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md glass font-semibold hover:scale-105 transition-transform"
              >
                <Layers className="size-4" />
                All Expertise Domains
              </Link>
            </div>
          </div>
        </div>
      </Section>

      <RelatedPages
        title="Continue exploring"
        items={[
          { title: "Expertise Hub", description: "The complete map of fifteen expertise domains. One integrated operating system.", href: "/expertise/", icon: Lock, accent: "denim" },
          { title: "Crisis Management", description: "The crisis transformation engine. Compressing damage windows and recovering in 30-60 days.", href: "/expertise/crisis-management/", icon: Shield, accent: "amber-gold" },
          { title: "Global Operations", description: "Sovereign-scale execution across continents. Eighteen countries, one unified command.", href: "/expertise/global-operations/", icon: Activity, accent: "emerald" },
          { title: "About Dr. Jyoti Kush", description: "The physician who became an operator. A decade of sovereign-scale leadership.", href: "/about/", icon: Crown, accent: "purple" },
        ]}
      />
    </>
  );
}
